import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
  Button,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Skeleton,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../../hooks/useTheme';
import { THEME_COLORS, Z_INDEX } from '../../constants/theme';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import './DataTable.css';

/**
 * Enhanced DataTable Component with Material Design
 * 
 * Features:
 * - Full light/dark theme support
 * - Sorting, pagination, filtering
 * - Responsive design
 * - Custom cell rendering
 * - Sticky headers
 * 
 * @component
 * @example
 * <DataTable
 *   columns={columns}
 *   data={data}
 *   loading={loading}
 *   pagination={{ page: 0, perPage: 10, totalRecords: 100 }}
 *   onPageChange={handlePageChange}
 *   onPerPageChange={handlePerPageChange}
 *   onSortChange={handleSortChange}
 *   sortConfig={{ column: 'name', direction: 'asc' }}
 * />
 */
const DataTable = ({
  columns = [],
  data = [],
  loading = false,
  pagination = {
    page: 0,
    perPage: 10,
    totalRecords: 0,
  },
  onPageChange,
  onPerPageChange,
  onSortChange,
  sortConfig = { column: null, direction: 'asc' },
  emptyMessage = 'No data available',
  stickyHeader = false,
  className = '',
  filterRow = null, // Optional filter row component
  toolbarRight = null, // Optional node (e.g. Add button) on the right of the rows-per-page row
  optimizeScroll = false, // Reduce row hover/transition work for heavy tables
  skeletonRowCount = 0, // When > 0, show skeleton rows instead of spinner during initial load
  onRowClick = null,
}) => {
  const { isDark } = useCustomTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints?.down('sm') || '(max-width: 600px)');

  const skeletonBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)';

  const renderSkeletonRows = () => {
    const rowCount = skeletonRowCount > 0 ? skeletonRowCount : pagination.perPage || 10;
    return Array.from({ length: rowCount }).map((_, rowIndex) => (
      <TableRow key={`skeleton-row-${rowIndex}`}>
        {columns.map((column) => (
          <TableCell
            key={`${column.field}-sk-${rowIndex}`}
            align={column.align || 'left'}
            sx={column.sx}
          >
            {column.renderSkeleton ? (
              column.renderSkeleton({ column, isDark, skeletonBg })
            ) : column.field === 'actions' || column.field === 'history' ? (
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', justifyContent: column.align === 'center' ? 'center' : 'flex-start' }}>
                <Skeleton
                  variant="rounded"
                  width={32}
                  height={32}
                  animation="wave"
                  sx={{ bgcolor: skeletonBg, borderRadius: 1.25 }}
                />
              </Box>
            ) : (
              <Skeleton
                variant="text"
                animation="wave"
                height={24}
                sx={{
                  bgcolor: skeletonBg,
                  width: column.field === 'invoice_number' || column.field === 'booking_code' ? '72%' : '58%',
                }}
              />
            )}
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  const showSkeletonBody = loading && skeletonRowCount > 0 && data.length === 0;
  const showInitialSpinner = loading && !showSkeletonBody && data.length === 0;

  const handleSort = (column) => {
    if (!column.sortable) return;
    
    const newDirection =
      sortConfig.column === column.field && sortConfig.direction === 'asc'
        ? 'desc'
        : 'asc';
    
    onSortChange?.({
      column: column.field,
      direction: newDirection,
    });
  };

  const handleChangePage = (event, newPage) => {
    onPageChange?.(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newPerPage = parseInt(event.target.value, 10);
    onPerPageChange?.(newPerPage);
  };

  // Render cell content
  const renderCell = (row, column) => {
    const value = column.accessor
      ? typeof column.accessor === 'function'
        ? column.accessor(row)
        : row[column.accessor]
      : row[column.field];

    if (column.render) {
      return column.render(value, row);
    }

    return value ?? '—';
  };

  // Calculate pagination info
  const paginationInfo = useMemo(() => {
    const start = pagination.page * pagination.perPage + 1;
    const end = Math.min((pagination.page + 1) * pagination.perPage, pagination.totalRecords);
    const totalPages = Math.ceil(pagination.totalRecords / pagination.perPage);
    return { start, end, totalPages };
  }, [pagination]);

  // Generate page numbers for pagination
  const pageNumbers = useMemo(() => {
    const { totalPages } = paginationInfo;
    const currentPage = pagination.page + 1;
    const maxVisiblePages = isMobile ? 5 : 10;
    const pages = [];
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 5) {
        for (let i = 1; i <= 7; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 4) {
        pages.push(1);
        pages.push('ellipsis');
        for (let i = totalPages - 6; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('ellipsis');
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages);
      }
    }
    
    return pages;
  }, [paginationInfo, pagination.page, isMobile]);

  return (
    <Box 
      className={`data-table-container ${className}`}
      sx={{
        width: '100%',
        mt: 0,
        overflow: 'visible',
      }}
    >
      {/* Toolbar row: rows per page (left) and optional toolbarRight (right) */}
      {(!loading && data.length > 0) ||
      (loading && skeletonRowCount > 0) ||
      pagination.totalRecords > 0 ||
      toolbarRight ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {pagination.totalRecords > 0 || (!loading && data.length > 0) ? (
              <>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    color: isDark ? THEME_COLORS.TEXT_SECONDARY_DARK : THEME_COLORS.TEXT_SECONDARY_LIGHT,
                  }}
                >
                  {isMobile ? 'Rows:' : 'Rows per page:'}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={pagination.perPage}
                    onChange={handleChangeRowsPerPage}
                    disabled={loading}
                    sx={{
                      height: '32px',
                      fontSize: '0.875rem',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: isDark ? THEME_COLORS.DIVIDER_DARK : THEME_COLORS.DIVIDER_LIGHT,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: THEME_COLORS.PRIMARY,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: THEME_COLORS.PRIMARY,
                      },
                      color: isDark ? THEME_COLORS.TEXT_PRIMARY_DARK : THEME_COLORS.TEXT_PRIMARY_LIGHT,
                      '& .MuiSelect-select': {
                        py: 0.5,
                        color: isDark ? THEME_COLORS.TEXT_PRIMARY_DARK : THEME_COLORS.TEXT_PRIMARY_LIGHT,
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </>
            ) : loading && skeletonRowCount > 0 ? (
              <Typography
                variant="body2"
                component="span"
                sx={{
                  color: isDark ? THEME_COLORS.TEXT_SECONDARY_DARK : THEME_COLORS.TEXT_SECONDARY_LIGHT,
                }}
              >
                Loading {skeletonRowCount} rows…
              </Typography>
            ) : null}
          </Box>
          {toolbarRight ? <Box sx={{ ml: 'auto' }}>{toolbarRight}</Box> : null}
        </Box>
      ) : null}

      {/* Table Container */}
      <Box className="table-responsive">
        <TableContainer
          className="data-table-wrapper"
          sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
            boxShadow: 'none',
            background: 'transparent',
            borderRadius: 0,
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: isDark ? THEME_COLORS.BACKGROUND_DARK : '#f5f5f5',
            },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '4px',
              background: isDark ? '#555' : '#ccc',
              '&:hover': {
                background: isDark ? '#777' : '#999',
              },
            },
          }}
        >
          <Table
            stickyHeader={stickyHeader}
            className="data-table table table-hover mb-0"
            sx={{
              minWidth: 650,
              marginBottom: 0,
              backgroundColor: 'transparent',
              '& .MuiTableCell-root': {
                padding: isMobile ? '8px 4px' : '12px 10px',
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                borderBottom: `1px solid ${isDark ? THEME_COLORS.DIVIDER_DARK : THEME_COLORS.DIVIDER_LIGHT}`,
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                backgroundColor: 'transparent',
                position: stickyHeader ? 'sticky' : 'relative',
                top: stickyHeader ? 0 : 'auto',
                zIndex: Z_INDEX.HEADER - 1,
              },
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.field}
                    align={column.align || 'left'}
                    className="data-table-header"
                    sx={{
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '0.75rem',
                      color: isDark ? THEME_COLORS.TEXT_PRIMARY_DARK : THEME_COLORS.TEXT_PRIMARY_LIGHT,
                      borderBottom: `1px solid ${isDark ? THEME_COLORS.DIVIDER_DARK : THEME_COLORS.DIVIDER_LIGHT}`,
                      '& .MuiTableSortLabel-root': {
                        color: isDark ? THEME_COLORS.TEXT_PRIMARY_DARK : THEME_COLORS.TEXT_PRIMARY_LIGHT,
                        '&:hover': {
                          color: THEME_COLORS.PRIMARY,
                        },
                        '&.Mui-active': {
                          color: THEME_COLORS.PRIMARY,
                          '& .MuiTableSortLabel-icon': {
                            color: THEME_COLORS.PRIMARY,
                          },
                        },
                      },
                      ...(column.sx || {}),
                    }}
                  >
                    {column.sortable !== false ? (
                      <TableSortLabel
                        active={sortConfig.column === column.field}
                        direction={
                          sortConfig.column === column.field
                            ? sortConfig.direction
                            : 'asc'
                        }
                        onClick={() => handleSort(column)}
                      >
                        {column.label || column.field}
                      </TableSortLabel>
                    ) : (
                      column.label || column.field
                    )}
                  </TableCell>
                ))}
              </TableRow>
              {filterRow && (
                <TableRow className="filter-row">
                  {typeof filterRow === 'function' 
                    ? filterRow(columns)
                    : Array.isArray(filterRow)
                    ? filterRow.map((filterCell, index) => (
                        <TableCell
                          key={index}
                          className="filter-cell"
                          sx={{
                            backgroundColor: 'transparent',
                            padding: '8px',
                            borderBottom: `1px solid ${isDark ? THEME_COLORS.DIVIDER_DARK : THEME_COLORS.DIVIDER_LIGHT}`,
                            ...(columns[index]?.sx || {}),
                          }}
                        >
                          {filterCell}
                        </TableCell>
                      ))
                    : filterRow
                  }
                </TableRow>
              )}
            </TableHead>
            <TableBody
              sx={{
                opacity: loading && data.length > 0 ? 0.58 : 1,
                transition: 'opacity 0.15s ease',
                pointerEvents: loading && data.length > 0 ? 'none' : 'auto',
              }}
            >
              {showSkeletonBody ? (
                renderSkeletonRows()
              ) : showInitialSpinner ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    sx={{ py: 8 }}
                  >
                    <CircularProgress 
                      size={40} 
                      sx={{ color: THEME_COLORS.PRIMARY }}
                    />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mt: 2,
                        color: isDark ? THEME_COLORS.TEXT_SECONDARY_DARK : THEME_COLORS.TEXT_SECONDARY_LIGHT,
                      }}
                    >
                      Loading...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    sx={{ py: 8 }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{
                        color: isDark ? THEME_COLORS.TEXT_SECONDARY_DARK : THEME_COLORS.TEXT_SECONDARY_LIGHT,
                      }}
                    >
                      {emptyMessage}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, index) => (
                  <TableRow
                    key={row.id || index}
                    hover={!optimizeScroll || Boolean(onRowClick)}
                    className="data-table-row"
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    sx={{
                      cursor: onRowClick || !optimizeScroll ? 'pointer' : 'default',
                      transition: optimizeScroll && !onRowClick ? 'none' : 'background-color 0.3s ease',
                      ...(optimizeScroll && !onRowClick
                        ? {}
                        : {
                            '&:hover': {
                              backgroundColor: isDark
                                ? 'rgba(255, 255, 255, 0.02)'
                                : 'rgba(0, 0, 0, 0.02)',
                            },
                          }),
                      '& .MuiTableCell-root': {
                        color: isDark ? THEME_COLORS.TEXT_SECONDARY_DARK : THEME_COLORS.TEXT_PRIMARY_LIGHT,
                      },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.field}
                        align={column.align || 'left'}
                        className="data-table-cell"
                        sx={column.sx}
                      >
                        {renderCell(row, column)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Pagination Footer */}
      {(pagination.totalRecords > 0 || (!loading && data.length > 0)) && (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mt: 3,
            flexWrap: 'wrap', 
            gap: 2,
            opacity: loading ? 0.72 : 1,
          }}
        >
          {/* Pagination info - left */}
          <Typography 
            variant="body2" 
            component="span"
            sx={{
              color: isDark ? THEME_COLORS.TEXT_SECONDARY_DARK : THEME_COLORS.TEXT_SECONDARY_LIGHT,
            }}
          >
            Showing {paginationInfo.start} to {paginationInfo.end} of {pagination.totalRecords}
          </Typography>
          
          {/* Pagination controls - right */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={(e) => handleChangePage(e, pagination.page - 1)}
              disabled={loading || pagination.page === 0}
              size="small"
              sx={{
                border: `1px solid ${isDark ? THEME_COLORS.DIVIDER_DARK : THEME_COLORS.DIVIDER_LIGHT}`,
                color: isDark ? THEME_COLORS.TEXT_PRIMARY_DARK : THEME_COLORS.TEXT_PRIMARY_LIGHT,
                '&:hover': {
                  borderColor: THEME_COLORS.PRIMARY,
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                },
                '&:disabled': {
                  opacity: 0.5,
                  borderColor: isDark ? THEME_COLORS.DIVIDER_DARK : THEME_COLORS.DIVIDER_LIGHT,
                },
              }}
            >
              <ChevronLeft />
            </IconButton>
            
            {/* Page number buttons */}
            {pageNumbers.map((page, index) => {
              if (page === 'ellipsis') {
                return (
                  <Typography 
                    key={`ellipsis-${index}`} 
                    variant="body2" 
                    sx={{ 
                      px: 1, 
                      color: isDark ? THEME_COLORS.TEXT_SECONDARY_DARK : THEME_COLORS.TEXT_SECONDARY_LIGHT,
                    }}
                  >
                    ...
                  </Typography>
                );
              }
              
              const isActive = page === pagination.page + 1;
              return (
                <Button
                  key={page}
                  onClick={() => handleChangePage(null, page - 1)}
                  variant={isActive ? 'contained' : 'outlined'}
                  size="small"
                  disabled={loading}
                  sx={{
                    minWidth: '32px',
                    height: '32px',
                    padding: 0,
                    fontSize: '0.875rem',
                    ...(isActive && {
                      backgroundColor: THEME_COLORS.PRIMARY,
                      color: '#000',
                      '&:hover': {
                        backgroundColor: THEME_COLORS.PRIMARY_HOVER,
                      },
                    }),
                    ...(!isActive && {
                      borderColor: isDark ? THEME_COLORS.DIVIDER_DARK : THEME_COLORS.DIVIDER_LIGHT,
                      color: isDark ? THEME_COLORS.TEXT_PRIMARY_DARK : THEME_COLORS.TEXT_PRIMARY_LIGHT,
                      '&:hover': {
                        borderColor: THEME_COLORS.PRIMARY,
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                      },
                    }),
                  }}
                >
                  {page}
                </Button>
              );
            })}
            
            <IconButton
              onClick={(e) => handleChangePage(e, pagination.page + 1)}
              disabled={
                loading || (pagination.page + 1) * pagination.perPage >= pagination.totalRecords
              }
              size="small"
              sx={{
                border: `1px solid ${isDark ? THEME_COLORS.DIVIDER_DARK : THEME_COLORS.DIVIDER_LIGHT}`,
                color: isDark ? THEME_COLORS.TEXT_PRIMARY_DARK : THEME_COLORS.TEXT_PRIMARY_LIGHT,
                '&:hover': {
                  borderColor: THEME_COLORS.PRIMARY,
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                },
                '&:disabled': {
                  opacity: 0.5,
                  borderColor: isDark ? THEME_COLORS.DIVIDER_DARK : THEME_COLORS.DIVIDER_LIGHT,
                },
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DataTable;

